import type { User } from '@cutr/constants/user';

/** Data that belongs to the order itself. */
export const orderKeys = {
  root: ['order'] as const,
  all: (orderId: string) => [...orderKeys.root, orderId] as const,
};

/** Data that belongs to one variant of an order. A sibling of `orderKeys`, not a child. */
export const orderVariantKeys = {
  root: ['order-variant'] as const,
  all: (variantId: string) => [...orderVariantKeys.root, variantId] as const,
};

/** Reference data that belongs to the installation and outlives any order. */
export const catalogueKeys = {
  root: ['catalogue'] as const,
};

export const commentKeys = {
  list: (orderId: string) =>
    [...orderKeys.all(orderId), 'comments', 'list'] as const,
};

export const conversationKeys = {
  all: (orderId: string) =>
    [...orderKeys.all(orderId), 'conversations'] as const,
  list: (orderId: string) =>
    [...conversationKeys.all(orderId), 'list'] as const,
  details: (orderId: string) =>
    [...conversationKeys.all(orderId), 'detail'] as const,
  detail: (orderId: string, conversationId: string) =>
    [...conversationKeys.details(orderId), conversationId] as const,
  summaries: (orderId: string) =>
    [...conversationKeys.all(orderId), 'summary'] as const,
  summary: (orderId: string, conversationId: string) =>
    [...conversationKeys.summaries(orderId), conversationId] as const,
};

export const orderOwnerKeys = {
  list: (orderId: string) =>
    [...orderKeys.all(orderId), 'owners', 'list'] as const,
};

export const orderAddressKeys = {
  detail: (orderId: string) =>
    [...orderKeys.all(orderId), 'addresses'] as const,
};

export const orderFeatureKeys = {
  list: (orderId: string) =>
    [...orderKeys.all(orderId), 'features', 'list'] as const,
};

export const materialSwatchKeys = {
  list: (orderId: string) =>
    [...orderKeys.all(orderId), 'material-swatches', 'list'] as const,
};

export const hardwareSwatchKeys = {
  list: (orderId: string) =>
    [...orderKeys.all(orderId), 'hardware-swatches', 'list'] as const,
};

export const offerKeys = {
  all: (orderId: string) => [...orderKeys.all(orderId), 'offers'] as const,
  list: (orderId: string) => [...offerKeys.all(orderId), 'list'] as const,
};

export const orderIssueKeys = {
  all: (orderId: string) => [...orderKeys.all(orderId), 'issues'] as const,
  list: (orderId: string) => [...orderIssueKeys.all(orderId), 'list'] as const,
  tasks: (orderId: string, orderIssueId: string) =>
    [...orderIssueKeys.all(orderId), 'tasks', orderIssueId] as const,
};

export const scopeAnnotationKeys = {
  all: () => [...orderKeys.root, 'file'] as const,
  forFile: (fileId: string) =>
    [...scopeAnnotationKeys.all(), fileId, 'scope-annotations'] as const,
};

export const orderFileKeys = {
  all: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'files'] as const,
  list: (variantId: string) =>
    [...orderFileKeys.all(variantId), 'list'] as const,
  archived: (variantId: string) =>
    [...orderFileKeys.all(variantId), 'archived'] as const,
  replaced: (variantId: string) =>
    [...orderFileKeys.all(variantId), 'replaced-revisions'] as const,
};

/** Matches any files key of any variant — for cache sweeps that span variants. */
export function isOrderFilesKey(queryKey: readonly unknown[]) {
  return queryKey[0] === orderVariantKeys.root[0] && queryKey[2] === 'files';
}

export const orderFolderKeys = {
  structure: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'folders'] as const,
};

export const positionKeys = {
  all: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'positions'] as const,
  list: (variantId: string) =>
    [...positionKeys.all(variantId), 'list'] as const,
  archived: (variantId: string) =>
    [...positionKeys.all(variantId), 'archived'] as const,
  summary: (variantId: string) =>
    [...positionKeys.all(variantId), 'summary'] as const,
};

export const partKeys = {
  all: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'parts'] as const,
  list: (variantId: string) => [...partKeys.all(variantId), 'list'] as const,
};

export const partOperationKeys = {
  list: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'part-operations'] as const,
};

export const orderHardwareKeys = {
  list: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'hardware'] as const,
};

export const nestingKeys = {
  all: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'nesting'] as const,
  jobs: (variantId: string) => [...nestingKeys.all(variantId), 'jobs'] as const,
  packing: (variantId: string) =>
    [...nestingKeys.all(variantId), 'packing'] as const,
};

export const pricingChangeKeys = {
  all: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'pricing-changes'] as const,
  list: (variantId: string) =>
    [...pricingChangeKeys.all(variantId), 'list'] as const,
};

export const materialRequestKeys = {
  list: (variantId: string) =>
    [...orderVariantKeys.all(variantId), 'material-requests'] as const,
};

export const curatedMaterialKeys = {
  list: () => [...catalogueKeys.root, 'curated-materials'] as const,
};

export const curatedHardwareKeys = {
  list: () => [...catalogueKeys.root, 'curated-hardware'] as const,
};

export const hardwareCatalogueKeys = {
  list: () => [...catalogueKeys.root, 'hardware'] as const,
};

export const materialKeys = {
  list: () => [...catalogueKeys.root, 'materials'] as const,
};

export const partnerOrganisationKeys = {
  list: () => [...catalogueKeys.root, 'partner-organisations', 'list'] as const,
  annualGmv: () =>
    [...catalogueKeys.root, 'partner-organisations', 'annual-gmv'] as const,
  profile: (organisationId: string) =>
    [
      ...catalogueKeys.root,
      'partner-organisations',
      'profile',
      organisationId,
    ] as const,
  detail: (organisationId: string) =>
    [
      ...catalogueKeys.root,
      'partner-organisations',
      'detail',
      organisationId,
    ] as const,
};

export const customerOrganisationKeys = {
  list: () =>
    [...catalogueKeys.root, 'customer-organisations', 'list'] as const,
  profile: (customerOrganisationId: string) =>
    [
      ...catalogueKeys.root,
      'customer-organisations',
      'profile',
      customerOrganisationId,
    ] as const,
  detail: (customerOrganisationId: string) =>
    [
      ...catalogueKeys.root,
      'customer-organisations',
      'detail',
      customerOrganisationId,
    ] as const,
};

/** Whether an id names a partner or a customer organisation; it never changes. */
export const organisationKindKeys = {
  detail: (organisationId: string) =>
    [...catalogueKeys.root, 'organisation-kind', organisationId] as const,
};

// An absent type is its own entry: unfiltered returns every non-`Unauthenticated` user.
export const userKeys = {
  all: () => [...catalogueKeys.root, 'users'] as const,
  list: (type?: User['type']) =>
    [...userKeys.all(), 'list', type ?? 'all'] as const,
};

export const platformConfigKeys = {
  frontend: () =>
    [...catalogueKeys.root, 'platform-config', 'frontend'] as const,
};

/**
 * Request-for-quote pages. Their own root: an RFQ page lives outside the Order
 * page, so neither order root's lifecycle applies to it.
 */
export const rfqKeys = {
  root: ['rfq'] as const,
  list: () => [...rfqKeys.root, 'list'] as const,
  partnerStats: () => [...rfqKeys.root, 'partner-stats'] as const,
  all: (rfqId: string) => [...rfqKeys.root, rfqId] as const,
  // The admin and the maker read the same URL and get different shapes.
  adminDetail: (rfqId: string) => [...rfqKeys.all(rfqId), 'admin'] as const,
  partnerDetail: (rfqId: string) => [...rfqKeys.all(rfqId), 'partner'] as const,
};

// Not cache keys: they name a mutation so `useMutationState` and
// `useIsMutating` can find it without a per-row observer.
export const toggleFeatureMutationKey = [
  ...orderKeys.root,
  'features',
  'toggle',
] as const;

export const assignHardwareSwatchMutationKey = [
  ...orderKeys.root,
  'hardware-swatches',
  'assign',
] as const;

export const orderIssueUpdateMutationKey = [
  ...orderKeys.root,
  'issues',
  'update',
] as const;

export const orderIssueStatusMutationKey = [
  ...orderKeys.root,
  'issues',
  'status',
] as const;

// Every write to an issue's tasks shares this prefix, so one `isMutating` call finds them all.
export const orderIssueTaskWritesKey = [
  ...orderKeys.root,
  'issue-tasks',
] as const;

export const updateOrderIssueTaskMutationKey = [
  ...orderIssueTaskWritesKey,
  'update',
] as const;

export const addOrderIssueTaskImageMutationKey = [
  ...orderIssueTaskWritesKey,
  'add-image',
] as const;

export const deleteOrderIssueTaskImageMutationKey = [
  ...orderIssueTaskWritesKey,
  'delete-image',
] as const;

export const conversationMutationKeys = {
  sendMessage: ['conversations', 'send-message'] as const,
  markViewed: ['conversations', 'mark-viewed'] as const,
  setLabels: ['conversations', 'set-labels'] as const,
  create: ['conversations', 'create'] as const,
  remove: ['conversations', 'remove'] as const,
};
